/*
 * gazeAdapter.js — Gaze Report v6 data adapter (PURE).
 *
 * v2 = v1 field mapping (unchanged) + an OPTIONAL, purely-presentational INTERPRETATION
 * layer that supplies green/amber/red status and reference-band inputs for the Mobility-style
 * visual language.
 *
 * CRITICAL separation of concerns:
 *   - MEASUREMENT comes from the LOCKED v5 engine (never modified). This adapter only maps it.
 *   - STATUS / REFERENCE BANDS are REPORT-LAYER inputs, supplied by the caller in
 *     `interpretation` (per assessment). The adapter NEVER derives status from clinical
 *     thresholds and the v5 engine supplies none. If `interpretation` is absent, the report
 *     renders values with NEUTRAL status (no color claim) — exactly like v1's honesty.
 *
 * Status values are the Mobility vocabulary: "good" | "average" | "poor" (green/amber/red),
 * plus "none" (neutral / not-yet-interpreted). These are VISUAL DESIGN states, not validated
 * clinical cutoffs. The standalone demo supplies mock statuses so the full UX is reviewable.
 *
 * INPUT (see ENGINEER_HANDOFF): { assessments:[{ parent_report_id, subject_id,
 *   assessment_date|assessment_order, pro, anti, calibration?, interpretation? }] }
 * `interpretation` (all optional):
 *   {
 *     overall_status: "good"|"average"|"poor"|"none",
 *     overall_label:  "…",           // free text context line (demo only)
 *     domains: { timing|consistency|direction: "good"|"average"|"poor"|"none" },
 *     metrics: {
 *       <metricKey>: {
 *         status: "good"|"average"|"poor"|"none",
 *         // optional reference bar (presentation only): ordered zones + where the value sits
 *         reference?: { direction:"lowGood"|"highGood",
 *                       dividers:[<num>,<num>], min:<num>, max:<num>, unit:"ms"|"%" }
 *       }, ...
 *     }
 *   }
 */
(function (root, factory) {
  var api = factory();
  // eslint-disable-next-line no-undef
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  root.GazeAdapter = api;
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  function isNum(v) {
    return typeof v === "number" && isFinite(v);
  }
  function num(v) {
    return isNum(v) ? v : null;
  }
  function round1(v) {
    return isNum(v) ? Math.round(v * 10) / 10 : null;
  }
  function pct(fraction) {
    return isNum(fraction) ? round1(fraction * 100) : null;
  }
  function trimZero(r) {
    return String(Math.round(r * 10) / 10);
  }

  function metricsOf(t) {
    if (!t) return null;
    return t.metrics && typeof t.metrics === "object" ? t.metrics : t;
  }
  // Valid per-trial latencies for the within-session scatter. pro: target trials; anti: correct trials only.
  function validLatencies(m, kind) {
    if (!m || !Array.isArray(m.per_trial)) return [];
    return m.per_trial
      .filter(function (tr) {
        if (!isNum(tr.latency_ms)) return false;
        return kind === "pro"
          ? tr.direction === "target"
          : tr.direction === "correct";
      })
      .map(function (tr) {
        return tr.latency_ms;
      });
  }
  function provFlag(t, m) {
    if (t && typeof t.provisional_timing === "boolean")
      return t.provisional_timing;
    if (m && typeof m.provisional_timing === "boolean")
      return m.provisional_timing;
    return true;
  }

  var VALID_STATUS = { good: 1, average: 1, poor: 1, none: 1 };
  function normStatus(s) {
    return s && VALID_STATUS[s] ? s : "none";
  }

  // ---- ordering / history eligibility ----------------------------------
  function canOrder(list) {
    if (!list.length) return false;
    return list.every(function (a) {
      var hasSubject = a.subject_id != null && a.subject_id !== "";
      var hasOrder =
        (a.assessment_date != null && a.assessment_date !== "") ||
        isNum(a.assessment_order);
      return hasSubject && hasOrder;
    });
  }
  function orderKey(a) {
    if (a.assessment_date) {
      var t = Date.parse(a.assessment_date);
      if (!isNaN(t)) return t;
    }
    return isNum(a.assessment_order) ? a.assessment_order : 0;
  }

  var METRIC_DEFS = [
    { key: "pro_latency_ms", domain: "timing", unit: "ms" },
    { key: "anti_latency_ms", domain: "timing", unit: "ms" },
    { key: "anti_minus_pro_ms", domain: "timing", unit: "ms" },
    { key: "pro_sd_ms", domain: "consistency", unit: "ms" },
    { key: "anti_sd_ms", domain: "consistency", unit: "ms" },
    { key: "init_error_pct", domain: "direction", unit: "%" },
    { key: "correction_pct", domain: "direction", unit: "%" },
  ];

  function extractAssessment(a) {
    var proM = metricsOf(a.pro),
      antiM = metricsOf(a.anti);
    var proProv = a.pro ? provFlag(a.pro, proM) : true;
    var antiProv = a.anti ? provFlag(a.anti, antiM) : true;

    var proLat = proM ? num(proM.latency_median_ms) : null;
    var antiLat = antiM ? num(antiM.latency_median_ms) : null;
    var delta =
      isNum(antiLat) && isNum(proLat) ? round1(antiLat - proLat) : null;
    var proSd = proM ? num(proM.latency_sd_ms) : null;
    var antiSd = antiM ? num(antiM.latency_sd_ms) : null;
    var initErr = antiM ? num(antiM.init_error_rate) : null;
    var corrRate = antiM ? num(antiM.correction_rate) : null;

    var interp = a.interpretation || {};
    var interpMetrics = interp.metrics || {};
    function mStatus(key) {
      return normStatus((interpMetrics[key] || {}).status);
    }
    function mRef(key) {
      return (interpMetrics[key] || {}).reference || null;
    }

    var metrics = {
      pro_latency_ms: proLat,
      anti_latency_ms: antiLat,
      anti_minus_pro_ms: delta,
      pro_sd_ms: proSd,
      anti_sd_ms: antiSd,
      init_error_pct: pct(initErr),
      correction_pct: pct(corrRate),
    };
    var status = {},
      reference = {};
    METRIC_DEFS.forEach(function (d) {
      status[d.key] = mStatus(d.key);
      reference[d.key] = mRef(d.key);
    });

    var domains = interp.domains || {};
    var proLatArr = validLatencies(proM, "pro"),
      antiLatArr = validLatencies(antiM, "anti");
    return {
      parent_report_id: a.parent_report_id || null,
      subject_id: a.subject_id || null,
      subject_name: a.subject_name || null,
      subject_first_name:
        a.subject_first_name || a.first_name || a.firstName || null,
      subject_last_name:
        a.subject_last_name || a.last_name || a.lastName || null,
      staff_remark: a.staff_remark || null,
      series: {
        pro_latency_ms: proLatArr,
        pro_sd_ms: proLatArr,
        anti_latency_ms: antiLatArr,
        anti_sd_ms: antiLatArr,
      },
      assessment_date: a.assessment_date || null,
      assessment_order: isNum(a.assessment_order) ? a.assessment_order : null,
      timing_preliminary: proProv || antiProv,
      overall_status: normStatus(interp.overall_status),
      overall_label: interp.overall_label || null,
      domain_status: {
        timing: normStatus(domains.timing),
        consistency: normStatus(domains.consistency),
        direction: normStatus(domains.direction),
      },
      metrics: metrics,
      status: status,
      reference: reference,
      n: {
        pro_valid: proM ? num(proM.n_valid) : null,
        anti_valid: antiM ? num(antiM.n_valid) : null,
        anti_classifiable: antiM ? num(antiM.n_classifiable) : null,
        anti_genuine_error: antiM ? num(antiM.n_genuine_error) : null,
        anti_corrected: antiM ? num(antiM.n_corrected) : null,
      },
      additional: buildAdditional(proM, antiM, a.calibration),
      interpreted: !!a.interpretation,
    };
  }

  function buildAdditional(proM, antiM, calibration) {
    function taskExtra(m) {
      if (!m) return null;
      return {
        correction_latency_median_ms: num(m.correction_latency_median_ms),
        latency_iqr_ms: num(m.latency_iqr_ms),
        latency_cv: num(m.latency_cv),
        latency_mean_ms: num(m.latency_mean_ms),
        ambiguous_pct: num(m.ambiguous_pct),
        flags: {
          n_targets: num(m.n_targets),
          n_valid: num(m.n_valid),
          n_no_response: num(m.n_no_response),
          n_first_saccade_away: num(m.n_first_saccade_away),
          n_short_flag: num(m.n_short_flag),
          n_long_flag: num(m.n_long_flag),
          n_direction_scored: num(m.n_direction_scored),
          n_correct: num(m.n_correct),
          n_genuine_error: num(m.n_genuine_error),
          n_ambiguous: num(m.n_ambiguous),
          n_invalid_direction: num(m.n_invalid_direction),
          n_classifiable: num(m.n_classifiable),
          n_corrected: num(m.n_corrected),
        },
        rejections:
          m.rejections && typeof m.rejections === "object"
            ? m.rejections
            : null,
      };
    }
    var qc = null;
    if (
      calibration &&
      (calibration.qc || calibration.r2 || calibration.loto_rmse_deg)
    ) {
      var q = calibration.qc || {};
      qc = {
        calibration_error_deg: num(q.calibration_error_deg),
        fixation_jitter_deg: num(q.fixation_jitter_deg),
        calibration_blink_pct: num(q.calibration_blink_pct),
        r2_left: num(
          q.r2_left != null ? q.r2_left : calibration.r2 && calibration.r2.left,
        ),
        r2_right: num(
          q.r2_right != null
            ? q.r2_right
            : calibration.r2 && calibration.r2.right,
        ),
        loto_rmse_deg: calibration.loto_rmse_deg || null,
      };
    }
    return { pro: taskExtra(proM), anti: taskExtra(antiM), qc: qc };
  }

  function buildHistory(ordered) {
    var hist = {};
    METRIC_DEFS.forEach(function (def) {
      hist[def.key] = ordered.map(function (a) {
        return {
          label:
            a.assessment_date ||
            "#" + (a.assessment_order != null ? a.assessment_order : ""),
          value: a.metrics[def.key],
          status: a.status[def.key],
        };
      });
    });
    return hist;
  }
  function buildDomainHistory(ordered) {
    var out = { timing: [], consistency: [], direction: [] };
    ordered.forEach(function (a) {
      var label =
        a.assessment_date ||
        "#" + (a.assessment_order != null ? a.assessment_order : "");
      ["timing", "consistency", "direction"].forEach(function (id) {
        out[id].push({
          label: label,
          status: (a.domain_status && a.domain_status[id]) || "none",
        });
      });
    });
    return out;
  }
  function buildChangeVsPrevious(ordered) {
    var out = {},
      n = ordered.length,
      cur = ordered[n - 1],
      prev = n >= 2 ? ordered[n - 2] : null;
    METRIC_DEFS.forEach(function (def) {
      var c = cur.metrics[def.key],
        p = prev ? prev.metrics[def.key] : null;
      if (!isNum(c) || !isNum(p)) {
        out[def.key] = null;
        return;
      }
      var d = round1(c - p),
        unit = def.unit === "%" ? "pp" : "ms";
      out[def.key] =
        (d > 0 ? "+" : "") + trimZero(d) + " " + unit + " vs previous";
    });
    return out;
  }

  // Trend sessions already carry metrics in report units (percentages 0–100)
  // and backend status, so they map straight onto the history shape.
  function fromTrendSession(s) {
    var metrics = {},
      status = {},
      sm = s.metrics || {},
      ss = s.status || {},
      ds = s.domain_status || {};
    METRIC_DEFS.forEach(function (def) {
      metrics[def.key] = num(sm[def.key]);
      status[def.key] = normStatus(ss[def.key]);
    });
    return {
      assessment_date: s.assessment_date || null,
      assessment_order: isNum(s.assessment_order) ? s.assessment_order : null,
      metrics: metrics,
      status: status,
      domain_status: {
        timing: normStatus(ds.timing),
        consistency: normStatus(ds.consistency),
        direction: normStatus(ds.direction),
      },
    };
  }
  function trendHistory(sessions) {
    if (!Array.isArray(sessions) || sessions.length < 2) return null;
    return sessions.map(fromTrendSession);
  }

  function build(input) {
    var assessments;
    if (!input) assessments = [];
    else if (Array.isArray(input.assessments))
      assessments = input.assessments.slice();
    else if (input.pro || input.anti || input.parent_report_id)
      assessments = [input];
    else assessments = [];

    var extracted = assessments.map(extractAssessment);
    var orderable = canOrder(assessments);
    var ordered = extracted,
      historyAvailable = false;
    if (orderable && extracted.length >= 1) {
      var pairs = assessments.map(function (a, i) {
        return { raw: a, ex: extracted[i] };
      });
      pairs.sort(function (x, y) {
        return orderKey(x.raw) - orderKey(y.raw);
      });
      ordered = pairs.map(function (p) {
        return p.ex;
      });
      historyAvailable = ordered.length >= 2;
    }
    var current = ordered.length ? ordered[ordered.length - 1] : null;

    // The trends endpoint's sessions (validated, oldest first, ending at the
    // selected report) are the history source when the details response
    // carries only the selected assessment.
    var trend = trendHistory(input && input.trendSessions);
    if (current && trend) {
      ordered = trend;
      historyAvailable = true;
    }

    return {
      report_version: "gaze-report-v6",
      generated_from: "amci_v5_locked",
      interpretationProvided: !!(current && current.interpreted),
      historyAvailable: historyAvailable,
      qcAvailable: !!(current && current.additional && current.additional.qc),
      current: current,
      changeVsPrevious: historyAvailable
        ? buildChangeVsPrevious(ordered)
        : null,
      history: historyAvailable ? buildHistory(ordered) : null,
      domainHistory: historyAvailable ? buildDomainHistory(ordered) : null,
    };
  }

  return { build: build };
});
